import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import {
  Close as CloseIcon,
  Save as SaveIcon
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { collection, doc, getDoc, getDocs, setDoc } from "firebase/firestore";

import { db } from "../firebase";
import { getDuration, getFormattedDuration } from "./utils";

const colors = [
  { name: "Rouge", value: "#ff0000" },
  { name: "Gris", value: "#808080" },
  { name: "Vert", value: "#008000" },
  { name: "Bleu", value: "#0000ff" },
  { name: "Noir", value: "#000000" },
  { name: "Orange", value: "#ffa500" },
  { name: "Jaune", value: "#ffff00" },
  { name: "Violet", value: "#800080" }
];

const getInitialFormData = () => ({
  id: "",
  type: "",
  name: "",
  description: "",
  duration: "",
  price: 0,
  color: "",
  payableOnline: false,
  bookableOnline: false,
  payableInInstallments: false,
  payableInInstallments2x: false
});

const ProductForm = ({ open, handleClose, productId, userId, onSave }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState(getInitialFormData());
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProductAndCategories = async () => {
      if (!userId) return;

      setLoading(true);
      try {
        const categoriesRef = collection(db, `users/${userId}/categories`);
        const categoriesSnapshot = await getDocs(categoriesRef);
        setCategories(
          categoriesSnapshot.docs.map(currentDoc => ({
            id: currentDoc.id,
            ...currentDoc.data()
          }))
        );

        if (productId) {
          const productDoc = doc(db, `users/${userId}/catalog`, productId);
          const docSnap = await getDoc(productDoc);
          if (docSnap.exists()) {
            setFormData({ ...getInitialFormData(), ...docSnap.data(), id: productId });
          }
        } else {
          setFormData(getInitialFormData());
        }
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchProductAndCategories();
    }
  }, [open, productId, userId]);

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!userId) return;

    setLoading(true);
    const newProductId = productId || new Date().getTime().toString();
    const productRef = doc(db, `users/${userId}/catalog`, newProductId);

    try {
      await setDoc(productRef, { ...formData, id: newProductId }, { merge: true });
      enqueueSnackbar(t("catalog.notifications.saveSuccess"), { variant: "success" });
      onSave({ ...formData, id: newProductId });
      handleClose();
    } catch (error) {
      enqueueSnackbar(t("catalog.notifications.saveError") + error.message, {
        variant: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          py: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {productId ? t("catalog.form.editTitle") : t("catalog.form.createTitle")}
        </Typography>
        <IconButton onClick={handleClose} sx={{ backgroundColor: "action.hover" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, md: 4 }, py: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3} component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={t("catalog.type")}
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  fullWidth
                  required
                >
                  {categories.map(category => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("catalog.price")}
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("catalog.name")}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("catalog.duration")}
                  name="duration"
                  select
                  value={getFormattedDuration(formData.duration)}
                  onChange={handleChange}
                  fullWidth
                >
                  {getDuration().map(duration => (
                    <MenuItem key={duration.id} value={duration.name}>
                      {duration.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("catalog.description")}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={t("catalog.color")}
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  fullWidth
                >
                  {colors.map(color => (
                    <MenuItem key={color.value} value={color.value}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          width: 20,
                          height: 20,
                          backgroundColor: color.value,
                          borderRadius: "50%",
                          marginRight: 1.25,
                          border: "1px solid rgba(0, 0, 0, 0.25)"
                        }}
                      />
                      {color.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Divider />

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: theme =>
                  theme.palette.mode === "dark"
                    ? "rgba(148, 163, 184, 0.12)"
                    : "rgba(15, 23, 42, 0.03)"
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.25 }}>
                {t("catalog.options")}
              </Typography>
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.payableOnline}
                        onChange={handleChange}
                        name="payableOnline"
                      />
                    }
                    label={t("catalog.form.payableOnline")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.bookableOnline}
                        onChange={handleChange}
                        name="bookableOnline"
                      />
                    }
                    label={t("catalog.form.bookableOnline")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.payableInInstallments}
                        onChange={handleChange}
                        name="payableInInstallments"
                      />
                    }
                    label={t("catalog.form.payableInInstallments")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.payableInInstallments2x}
                        onChange={handleChange}
                        name="payableInInstallments2x"
                      />
                    }
                    label={t("catalog.form.payableInInstallments2x")}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 2,
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          justifyContent: "space-between"
        }}
      >
        <Button onClick={handleClose} color="inherit" sx={{ textTransform: "none" }}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
          endIcon={<SaveIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 2.5,
            py: 1
          }}
        >
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductForm;
